import qtiConstants from '../../../qti/Constants';
import { reportError } from '../../../services/ErrorReporting';

const { PDFJS_ROOT } = qtiConstants.staticLibPaths;

function getSameOriginRoot() {
    try {
        // Prefix with /player/file/ci so the URL:
        // 1. Falls within the service worker's scope (/player/)
        // 2. Matches the SW's handleFetch pattern (checks for '/file/ci' in URL)
        return `/player/file/ci${new URL(PDFJS_ROOT).pathname}`;
    } catch (e) {
        return PDFJS_ROOT;
    }
}

const PDFJSLoader = {
    getAllFilesToCache() {
        const sameOriginRoot = getSameOriginRoot();
        const webImagePaths = this.getViewerWebImagePaths();
        const imageEntries = webImagePaths.map(relPath => ({
            fetchUrl: `${PDFJS_ROOT}web/${relPath}`,
            url: `${sameOriginRoot}web/${relPath}`,
            type: 'image',
        }));
        return [
            { fetchUrl: `${PDFJS_ROOT}web/viewer.html`, url: `${sameOriginRoot}web/viewer.html`, type: 'html' },
            { fetchUrl: `${PDFJS_ROOT}build/pdf.js`, url: `${sameOriginRoot}build/pdf.js`, type: 'js' },
            { fetchUrl: `${PDFJS_ROOT}web/viewer.js`, url: `${sameOriginRoot}web/viewer.js`, type: 'js' },
            { fetchUrl: `${PDFJS_ROOT}web/viewer.css`, url: `${sameOriginRoot}web/viewer.css`, type: 'css' },
            // eslint-disable-next-line max-len
            { fetchUrl: `${PDFJS_ROOT}web/locale/locale.properties`, url: `${sameOriginRoot}web/locale/locale.properties`, type: 'properties' },
            // eslint-disable-next-line max-len
            { fetchUrl: `${PDFJS_ROOT}web/locale/nb-NO/viewer.properties`, url: `${sameOriginRoot}web/locale/nb-NO/viewer.properties`, type: 'properties' },
            // eslint-disable-next-line max-len
            { fetchUrl: `${PDFJS_ROOT}web/locale/en-US/viewer.properties`, url: `${sameOriginRoot}web/locale/en-US/viewer.properties`, type: 'properties' },
            // eslint-disable-next-line max-len
            { fetchUrl: `${PDFJS_ROOT}web/locale/en-GB/viewer.properties`, url: `${sameOriginRoot}web/locale/en-GB/viewer.properties`, type: 'properties' },
            { fetchUrl: `${PDFJS_ROOT}build/pdf.worker.js`, url: `${sameOriginRoot}build/pdf.worker.js`, type: 'js' },
            ...imageEntries,
        ];
    },

    /**
     * Relative paths (under web/) for all images referenced by viewer.css.
     * Cached so the offline blob-URL path can serve them as blob URLs instead
     * of trying to resolve relative paths from a blob: context.
     * Failures are handled gracefully in cacheViewerFiles (failed counter only).
     */
    getViewerWebImagePaths() {
        return [
            'images/shadow.png',
            'images/loading-icon.gif',
            'images/loading.svg',
            'images/treeitem-expanded.svg',
            'images/treeitem-collapsed.svg',
            'images/toolbarButton-menuArrow.svg',
            'images/toolbarButton-sidebarToggle.svg',
            'images/toolbarButton-secondaryToolbarToggle.svg',
            'images/toolbarButton-pageUp.svg',
            'images/toolbarButton-pageDown.svg',
            'images/toolbarButton-zoomOut.svg',
            'images/toolbarButton-zoomIn.svg',
            'images/toolbarButton-presentationMode.svg',
            'images/toolbarButton-print.svg',
            'images/toolbarButton-openFile.svg',
            'images/toolbarButton-download.svg',
            'images/toolbarButton-bookmark.svg',
            'images/toolbarButton-viewThumbnail.svg',
            'images/toolbarButton-viewOutline.svg',
            'images/toolbarButton-viewAttachments.svg',
            'images/toolbarButton-viewLayers.svg',
            'images/toolbarButton-search.svg',
            'images/findbarButton-previous.svg',
            'images/findbarButton-next.svg',
            'images/secondaryToolbarButton-firstPage.svg',
            'images/secondaryToolbarButton-lastPage.svg',
            'images/secondaryToolbarButton-rotateCcw.svg',
            'images/secondaryToolbarButton-rotateCw.svg',
            'images/secondaryToolbarButton-selectTool.svg',
            'images/secondaryToolbarButton-handTool.svg',
            'images/secondaryToolbarButton-scrollVertical.svg',
            'images/secondaryToolbarButton-scrollHorizontal.svg',
            'images/secondaryToolbarButton-scrollWrapped.svg',
            'images/secondaryToolbarButton-spreadNone.svg',
            'images/secondaryToolbarButton-spreadOdd.svg',
            'images/secondaryToolbarButton-spreadEven.svg',
            'images/secondaryToolbarButton-documentProperties.svg',
            'images/loading-dark.svg',
            'images/treeitem-expanded-dark.svg',
            'images/treeitem-collapsed-dark.svg',
            'images/toolbarButton-menuArrow-dark.svg',
            'images/toolbarButton-sidebarToggle-dark.svg',
            'images/toolbarButton-secondaryToolbarToggle-dark.svg',
            'images/toolbarButton-pageUp-dark.svg',
            'images/toolbarButton-pageDown-dark.svg',
            'images/toolbarButton-zoomOut-dark.svg',
            'images/toolbarButton-zoomIn-dark.svg',
            'images/toolbarButton-presentationMode-dark.svg',
            'images/toolbarButton-print-dark.svg',
            'images/toolbarButton-openFile-dark.svg',
            'images/toolbarButton-download-dark.svg',
            'images/toolbarButton-bookmark-dark.svg',
            'images/toolbarButton-viewThumbnail-dark.svg',
            'images/toolbarButton-viewOutline-dark.svg',
            'images/toolbarButton-viewAttachments-dark.svg',
            'images/toolbarButton-viewLayers-dark.svg',
            'images/toolbarButton-search-dark.svg',
            'images/findbarButton-previous-dark.svg',
            'images/findbarButton-next-dark.svg',
            'images/secondaryToolbarButton-firstPage-dark.svg',
            'images/secondaryToolbarButton-lastPage-dark.svg',
            'images/secondaryToolbarButton-rotateCcw-dark.svg',
            'images/secondaryToolbarButton-rotateCw-dark.svg',
            'images/secondaryToolbarButton-selectTool-dark.svg',
            'images/secondaryToolbarButton-handTool-dark.svg',
            'images/secondaryToolbarButton-scrollVertical-dark.svg',
            'images/secondaryToolbarButton-scrollHorizontal-dark.svg',
            'images/secondaryToolbarButton-scrollWrapped-dark.svg',
            'images/secondaryToolbarButton-spreadNone-dark.svg',
            'images/secondaryToolbarButton-spreadOdd-dark.svg',
            'images/secondaryToolbarButton-spreadEven-dark.svg',
            'images/secondaryToolbarButton-documentProperties-dark.svg',
        ];
    },

    /**
     * Proactively caches PDF.js viewer files into GENERAL_RESOURCES Cache Storage
     * so the service worker can serve them offline.
     *
     * Files are fetched from CDN (fetchUrl) and stored under a same-origin key (url)
     * that the service worker recognises via the /file/ci/ URL pattern.
     *
     * viewer.html is an HTML file and CloudFront does not emit CORS headers for it,
     * so a plain mode:'cors' fetch will throw a TypeError. We catch that and retry
     * with mode:'no-cors', which returns an opaque response (status 0) that can still
     * be stored in the Cache API and served by the service worker for iframe navigation.
     */
    async cacheViewerFiles() {
        const files = this.getAllFilesToCache();

        if (!('caches' in window)) {
            return;
        }

        try {
            const cache = await caches.open('GENERAL_RESOURCES');

            await Promise.all(files.map(async (file) => {
                try {
                    // Use ignoreVary so we can detect entries that were previously cached
                    // with a Vary header and need to be replaced.
                    const existing = await cache.match(file.url, { ignoreVary: true });
                    if (existing && !existing.headers.has('vary')) {
                        return;
                    }

                    let response;
                    try {
                        response = await fetch(file.fetchUrl);
                    } catch (err) {
                        response = await fetch(file.fetchUrl, { mode: 'no-cors' });
                    }

                    if (response.ok) {
                        // Strip the Vary header before storing. The CDN returns
                        // "Vary: Origin, accept-encoding" which causes cache.match() to fail
                        // in the service worker: the stored request (created from a URL string)
                        // has no Accept-Encoding header, but a real navigation/fetch request does,
                        // so the Vary check never matches and the SW falls through to the network.
                        const headers = new Headers(response.headers);
                        headers.delete('vary');

                        // For viewer.html: store a second readable copy under a dedicated key so
                        // the offline blob-URL path can rewrite its script references and serve
                        // the real PDFJS viewer without needing SW interception.
                        if (file.type === 'html') {
                            const readableClone = response.clone();
                            const readableHeaders = new Headers(readableClone.headers);
                            readableHeaders.delete('vary');
                            await cache.put(this.getViewerHtmlReadableKey(), new Response(readableClone.body, {
                                status: readableClone.status,
                                statusText: readableClone.statusText,
                                headers: readableHeaders,
                            }));
                        }

                        await cache.put(file.url, new Response(response.body, {
                            status: response.status,
                            statusText: response.statusText,
                            headers,
                        }));
                    } else if (response.type === 'opaque') {
                        // Opaque responses (e.g. viewer.html from CDN without CORS headers) cannot
                        // be reconstructed — new Response(null, {status: 0}) throws a RangeError
                        // because the Response constructor requires status 200-599. Store the
                        // original opaque response directly; the service worker can still serve
                        // it for iframe navigation even though JS cannot inspect its contents.
                        await cache.put(file.url, response);
                    }
                } catch (err) {
                    reportError(err);
                }
            }));
        } catch (err) {
            // caches.open() can reject in private browsing mode or when storage quota is exceeded.
            // Treat the whole operation as best-effort — a rejection must not surface as an
            // unhandled promise rejection when cacheViewerFiles() is called fire-and-forget.
            reportError(err);
        }
    },

    /**
     * Same-origin cached base URL for viewer web assets (images, etc.).
     * Used to look up cached images via caches.match() when rewriting
     * relative url() references in viewer.css for offline blob-URL serving.
     */
    getViewerWebCachedRoot() {
        return `${getSameOriginRoot()}web/`;
    },

    /**
     * CDN base URL for viewer web assets — fallback when an asset is not in cache.
     */
    getViewerWebCdnRoot() {
        return `${PDFJS_ROOT}web/`;
    },

    getPdfJsCachedUrl() {
        return `${getSameOriginRoot()}build/pdf.js`;
    },

    getPdfWorkerCachedUrl() {
        return `${getSameOriginRoot()}build/pdf.worker.js`;
    },

    getViewerJsCachedUrl() {
        return `${getSameOriginRoot()}web/viewer.js`;
    },

    getViewerCssCachedUrl() {
        return `${getSameOriginRoot()}web/viewer.css`;
    },

    /**
     * Key under which a readable (non-opaque) copy of viewer.html is stored.
     * Only present when the CDN returns CORS headers for viewer.html (i.e. in production
     * where the player origin is whitelisted). Falls back to the custom viewer when absent.
     */
    getViewerHtmlReadableKey() {
        return `${getSameOriginRoot()}web/viewer.html.readable`;
    },

    /**
     * No loading mechanism for PDFJS yet.
     * @return {Promise<boolean>}
     */
    async init() {
        return Promise.resolve(false);
    },
};

export default PDFJSLoader;
